import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  Inject,
  inject,
  signal,
  TemplateRef,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import getPaymentData, {
  PaymentData,
} from '@core/utils/payment-method.service';
import { getAriaTicketUrl } from '@core/utils/url';
import { OrderCancelData } from '@features/order-actions/models/order-action.model';
import {
  OrderCancellationPayload
} from '@features/order-actions/models/order-cancellation.model';
import { OrderCancelState } from '@features/order-actions/store/order-cancel.state';
import { HorusLoadingComponent } from '@shared/components/domain/horus-loading/horus-loading.component';
import { HorusMessageGeneralComponent } from '@shared/components/domain/horus-message-general/horus-message-general.component';
import { ConfirmV2DialogComponent } from '@shared/components/ui/confirm-v2-dialog/confirm-v2-dialog.component';
import { TenantUrlUtilService } from '@shared/services/tenant-url-util.service';
import { HorusBtnDirective } from '../../../../shared/directives/horus-btn.directive';

@Component({
  selector: 'app-order-cancel-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatSelectModule,
    HorusLoadingComponent,
    HorusMessageGeneralComponent,
    HorusBtnDirective,
  ],
  templateUrl: './order-cancel-dialog.component.html',
  styleUrl: './order-cancel-dialog.component.scss',
})
export class OrderCancelDialogComponent implements OnDestroy {
  @ViewChild('confirmTemplate') confirmTemplate!: TemplateRef<any>;

  dialogRef = inject(MatDialogRef<OrderCancelDialogComponent>);
  dialog = inject(MatDialog);
  state = inject(OrderCancelState);
  tenantUrlUtil = inject(TenantUrlUtilService);
  fb = inject(FormBuilder);
  orderCancelForm: FormGroup;
  paymentData: PaymentData;

  constructor(@Inject(MAT_DIALOG_DATA) public data: OrderCancelData) {
    this.orderCancelForm = this.fb.group({
      cancellationType: [null, Validators.required],
    });
    this.paymentData = getPaymentData(data.tenantId!, data.payment, 'assets');
  }

  ngOnInit(): void {
    this.state.orderId.set(this.data.orderId!);
    this.state.orderType.set(this.data.orderType!);
  }

  openConfirmOrderCancellation() {
    this.state.resetMutationState();
    const dialogConfirm = this.dialog.open(ConfirmV2DialogComponent, {
      height: '290px',
      width: '427px',
      panelClass: 'horus-confirmation-panel',
      data: {
        contentTemplate: this.confirmTemplate,
        close: () => {
          this.state.resetMutationState();
          dialogConfirm.close();
        },
      },
      disableClose: false,
      restoreFocus: false,
    });

    dialogConfirm.afterClosed().subscribe((result) => {});
  }

  reintentCancelOrder() {
    this.onConfirmOrderCancellation();
  }

  onConfirmOrderCancellation() {
    if (this.orderCancelForm.valid) {
      const tenantId = this.tenantUrlUtil.normalizedTenant();

      if (!tenantId) {
        return;
      }

      const payload: OrderCancellationPayload = {
        tenantId,
        orderId: this.data.orderId!,
        type: this.orderCancelForm.value.cancellationType,
        observation: '',
        user: this.tenantUrlUtil.agent(),
        channel: 'HORUS',
        saleNote: this.data.saleNoteNumber || '',
      };

      this.state.cancelOrderMutation.mutate(payload);
    }
  }

  closeAllDialogs() {
    this.state.resetMutationState();
    this.dialog.closeAll();
  }

  redirectToAria(): void {
    const tenantId = this.tenantUrlUtil.normalizedTenant()?.toUpperCase();
    if (tenantId) {
      const url = getAriaTicketUrl(tenantId, this.data.orderId!, this.data.orderType!);
      window.open(url, '_blank');
    }
  }

  retryFetchCancellationTypes(): void {
    this.state.refetchCancellationTypes();
  }

  ngOnDestroy(): void {
    this.state.resetMutationState();
  }
}
